/**
 * CompletedRideCard — driver post-trip fare-summary UI
 * (iter-72 decomposition slice 3).
 *
 * Extracted verbatim from Driver.jsx L1102–L1218. Renders a dismissible
 * "TRIP COMPLETE · final fare" green card between the active-ride
 * fading and the empty/idle state, so the driver gets a deliberate
 * "trip closed · here's the total" moment instead of a jarring snap
 * back to WAITING FOR OFFERS.
 *
 * Strict isolation contract:
 *   • `lastCompleted`         — data object, owned by parent (set by
 *                               the completion handler after the
 *                               backend returns the final fare).
 *   • `dismiss`               — parent setter, called when the driver
 *                               taps "Got it" (resets lastCompleted
 *                               to null so the card unmounts).
 *   • `markCashCollected`     — parent-owned API call (POST
 *                               /driver/mark-paid/{id}). Component
 *                               just renders the button; the entire
 *                               state-transition and HTTP request
 *                               stays in Driver.jsx.
 *
 * All 5 data-testids preserved verbatim:
 *   completed-ride-card · completed-ride-fare ·
 *   completed-ride-breakdown · completed-ride-vat ·
 *   completed-ride-payment-status · completed-ride-mark-paid-btn ·
 *   completed-ride-dismiss-btn
 *
 * Rollback: delete this file, paste the original ~116-line block
 * back into Driver.jsx between `{!activeRide && lastCompleted && (`
 * and the matching `)}`, remove the import. No coupling to slice 1
 * (ActiveRideCard) or slice 2 (PendingOfferCard).
 */
import React from "react";

export function CompletedRideCard({
  lastCompleted,
  dismiss,
  markCashCollected,
}) {
  return (
    <div
      className="border-4 border-[#10B981] bg-white p-5 space-y-3 shadow-[0_0_0_4px_rgba(16,185,129,0.18)]"
      data-testid="completed-ride-card"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="label-mini text-[#10B981] flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
            TRIP COMPLETE
          </div>
          <h3 className="font-heading text-xl font-black tracking-tight mt-1">
            {lastCompleted.passenger_name}
          </h3>
        </div>
        <div className="text-right">
          <div className="label-mini text-muted-foreground">FINAL FARE</div>
          <div className="font-mono-tab font-black text-3xl text-foreground tabular-nums" data-testid="completed-ride-fare">
            £{(lastCompleted.fare ?? 0).toFixed(2)}
          </div>
        </div>
      </div>

      <div className="text-xs space-y-1">
        <div className="flex items-start gap-2">
          <span className="w-2 h-2 rounded-full bg-[#10B981] mt-1.5 flex-shrink-0" />
          <span className="truncate">{lastCompleted.pickup_address}</span>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-2 h-2 rounded-full bg-[#EF4444] mt-1.5 flex-shrink-0" />
          <span className="truncate">{lastCompleted.dropoff_address}</span>
        </div>
      </div>

      {lastCompleted.breakdown && (
        <div className="border border-border bg-[#FAFAFB] p-3" data-testid="completed-ride-breakdown">
          <div className="label-mini text-muted-foreground mb-2">BREAKDOWN</div>
          <div className="text-xs space-y-1">
            <div className="flex justify-between font-mono-tab">
              <span className="text-muted-foreground">Base</span>
              <span className="font-bold">£{(lastCompleted.breakdown.base_fare ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-mono-tab">
              <span className="text-muted-foreground">Distance</span>
              <span className="font-bold">£{(lastCompleted.breakdown.distance_charge ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-mono-tab">
              <span className="text-muted-foreground">Time</span>
              <span className="font-bold">£{(lastCompleted.breakdown.time_charge ?? 0).toFixed(2)}</span>
            </div>
            {(lastCompleted.breakdown.extras?.total ?? 0) > 0 && (
              <div className="flex justify-between font-mono-tab">
                <span className="text-muted-foreground">Extras</span>
                <span className="font-bold">£{lastCompleted.breakdown.extras.total.toFixed(2)}</span>
              </div>
            )}
            {(lastCompleted.breakdown.late_night_amount ?? 0) > 0 && (
              <div className="flex justify-between font-mono-tab">
                <span className="text-muted-foreground">Late-night</span>
                <span className="font-bold">£{lastCompleted.breakdown.late_night_amount.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-border mt-1 pt-1 flex justify-between font-mono-tab">
              <span className="font-bold">Total (gross)</span>
              <span className="font-black">£{(lastCompleted.breakdown.gross ?? 0).toFixed(2)}</span>
            </div>
            {(lastCompleted.breakdown.vat_amount ?? 0) > 0 && (
              <div className="text-[10px] text-muted-foreground font-mono-tab" data-testid="completed-ride-vat">
                incl. £{lastCompleted.breakdown.vat_amount.toFixed(2)} VAT @ {Math.round((lastCompleted.breakdown.vat_rate ?? 0) * 100)}%
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-muted-foreground">
          Payment: <span
            className={`font-bold uppercase ${
              lastCompleted.payment_status === "paid" ? "text-[#10B981]"
              : lastCompleted.payment_status === "invoiced" ? "text-foreground"
              : "text-[#92400E]"
            }`}
            data-testid="completed-ride-payment-status"
          >
            {lastCompleted.payment_status === "paid"
              ? `PAID · ${(lastCompleted.payment_method || "cash").toUpperCase()}`
              : lastCompleted.payment_method === "cash"
                ? "CASH DUE · COLLECT FROM PASSENGER"
                : `${(lastCompleted.payment_status || "pending").toUpperCase()} · ${(lastCompleted.payment_method || "cash").toUpperCase()}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Cash-collected confirmation — only for cash rides that
              are still pending. Account/card flows don't get this
              button (account is invoiced, card is wired through
              a provider webhook). */}
          {lastCompleted.payment_method === "cash" && lastCompleted.payment_status !== "paid" && (
            <button
              type="button"
              onClick={markCashCollected}
              className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white text-xs font-bold uppercase tracking-wider px-3 py-2 transition-colors"
              data-testid="completed-ride-mark-paid-btn"
            >
              Confirm cash received
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="bg-[#10B981] hover:bg-[#059669] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 transition-colors"
            data-testid="completed-ride-dismiss-btn"
          >
            Got it
          </button>
        </div>
      </div>
      {lastCompleted.payment_method === "cash" && lastCompleted.payment_status !== "paid" && (
        <div
          className="text-[11px] text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2 mt-1"
          data-testid="completed-ride-cash-instruction"
        >
          Collect £{(lastCompleted.breakdown?.gross ?? lastCompleted.fare ?? 0).toFixed(2)} cash from the passenger, then tap <span className="font-bold">Confirm cash received</span> to close out this ride.
        </div>
      )}
    </div>
  );
}
